import React, { useState, useRef, useEffect } from "react";
import { saveAs } from "file-saver";
import {
  Box, Flex, Text, Button, VStack,
  Heading, Icon, Input,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { FiDownload, FiCalendar } from "react-icons/fi";
import { TbReportAnalytics } from "react-icons/tb";

const parseDatePayload = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return { date: d, month: m, year: y };
  };

 const HEADERS = [
     "Year",  "Month", "Region",
     "Outlet Type-Class",
      "Sub-Region", "Town Code","Shop   No", "Customer", "Outlet Type",
      "town_name", "store_name", "store_address", 
     "asm_name", "ps_ds_name", "so_se_name", "rsm_name",
    "Q9b. RGB Space Occupied by KO Product-Shelf 1",
    "Q9b. RGB Space Occupied by KO Product-Shelf 2",
    "Q9b. RGB Space Occupied by KO Product-Shelf 3",
    "Q9b. RGB Space Occupied by KO Product-Shelf 4",
    "Q9b. RGB Space Occupied by KO Product-Shelf 5",
    "Q9b. RGB Space Occupied by KO Product-Shelf 6",
    "Q9b. RGB Space Occupied by KO Product-Shelf 7",
    "Q9b. RGB Space Occupied by KO Product-Shelf 8",
    "Q9b. RGB Space Occupied by KO Product-Shelf 9",
    "Q9b. RGB Space Occupied by KO Product-Shelf 10",
    "Q10.1 Coke 250 ml RGB", "Q10.2 Sprite 250 ml RGB",
    "Q10.3 Fanta 250 ml RGB", "Q10.5 Kinley Soda 250 ml RGB",
    "Q10.9 Coke 250 ml PET", "Q10.10 Sprite 250 ml PET",
    "Q10.11 Fanta Orange 250 ml PET", "Q10.12 Fanta Lemon 250 ml PET",
    "Q12a.1 Coke ASSP 500ml", "Q12a.2 Sprite ASSP 500ml",
    "Q12a.3 Fanta Orange ASSP 500ml", "Q12a.7 Kinley Water 500/600/750ml",
    "Q13.1 Coke 1000 ML", "Q13.2 Sprite 1000 ML",
    "Q13.3 Fanta 1000 ML", "Q13.4 Kinley Water 1000 ML",
    "Q14.1 Coke FC1500", "Q14.2 Sprite FC1500", "Q14.3 Fanta FC1500",
    "Q15.1 Coke FC2000", "Q15.2 Sprite FC2000",
    "Q15.3 Fanta Orange FC2000", "Q15.4 Fanta Lemon FC2000",
    "Q16.1 Coke FC2250", "Q16.2 Sprite FC2250",
    "Q16.3 Fanta Orange FC2250", "Q16.4 Fanta Lemon FC2250",
    "Transactions", "Footfall",
  ];


export const handleStoresExport = async (from, to, setLoading) => {
    setLoading(true);

    const body = { from: parseDatePayload(from), to: parseDatePayload(to) };

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.REPORT_CENTER.STORERAWDATA}`,
        body
      );
      if (res.status !== 200) throw new Error("API failed: " + res.status);

      const list = Array.isArray(res.data) ? res.data : [res.data];

      // ── Same red header style as your other reports ──────────────────────
      const headerStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top:    { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left:   { style: "thin", color: { rgb: "000000" } },
          right:  { style: "thin", color: { rgb: "000000" } },
        },
      };
      const dataStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
        font: { color: { rgb: "2D3748" }, sz: 11 },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top:    { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left:   { style: "thin", color: { rgb: "000000" } },
          right:  { style: "thin", color: { rgb: "000000" } },
        },
      };

      // ── Build sheet data ─────────────────────────────────────────────────
      const wsData = [
        HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
        ...list.map((row) =>
          HEADERS.map((h) => ({
            v: row[h] !== undefined && row[h] !== null ? String(row[h]) : "",
            t: "s",
            s: dataStyle,
          }))
        ),
      ];

      const XLSX = await import("xlsx-js-style");
      const ws      = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"]   = HEADERS.map(() => ({ wch: 22 }));
      ws["!rows"]   = [{ hpt: 30 }, ...list.map(() => ({ hpt: 22 }))];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stores Raw Data");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Stores_Raw_Data_${from}_to_${to}.xlsx`
      );
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to generate report: " + err.message);
    } finally {
    setLoading(false);
    }
}


export default function StoresRawDataReport() {
  const [loading, setLoading] = useState(false);

  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day   = String(now.getDate()).padStart(2, "0");

  const [fromDate, setFromDate] = useState(`${year}-${month}-01`);
  const [toDate,   setToDate]   = useState(`${year}-${month}-${day}`);

  const fromDateRef = useRef(fromDate);
  const toDateRef   = useRef(toDate);
  useEffect(() => { fromDateRef.current = fromDate; }, [fromDate]);
  useEffect(() => { toDateRef.current   = toDate;   }, [toDate]);

  return (
    <Box bg="white" borderRadius="2xl" p={6} boxShadow="xl" border="1px solid" borderColor="gray.200">
      <Flex align="center" gap={3} mb={4}>
        <Icon as={TbReportAnalytics} boxSize={6} color="red.600" />
        <Heading size="md" color="gray.800">Stores Raw Data Report</Heading>
      </Flex>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Download complete raw store-level data including all SKU, shelf, and transaction columns.
      </Text>

      <VStack spacing={6} align="stretch">
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">From Date</Text>
            <Box position="relative">
              <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
              <Input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500" }}
              />
            </Box>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">To Date</Text>
            <Box position="relative">
              <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
              <Input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500" }}
              />
            </Box>
            <Button
            onClick={handleExport}
            isLoading={loading}
            loadingText="Generating..."
            size="lg"
            bg="red.600"
            color="white"
            leftIcon={<FiDownload />}
            borderRadius="xl"
            fontWeight="bold"
            _hover={{ bg: "red.700", transform: "translateY(-2px)" }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s"
        >
          Download Stores Raw Data
        </Button>
       </Box>
        </Flex>
      </VStack>
    </Box>
  );
}